import { toast } from 'sonner'

/**
 * Project-wide toast wrapper. Re-exports sonner's `toast` plus semantic
 * helpers that read with intent at the call site:
 *
 *   notify.ok('Reserva creada')
 *   notify.warn('Aún no hay datos del rango')
 *   notify.bad('No pudimos guardar los cambios')
 *   notify.info('Se aplicó la regla por defecto')
 *
 * **Action button** — every helper accepts an `action` slot via `ToastOptions`:
 *
 *   notify.ok('Reserva creada', { action: { label: 'Deshacer', onClick: () => undo() } })
 *   notify.bad('No se pudo guardar', { action: { label: 'Reintentar', onClick: retry } })
 *
 * Sonner renders the action as a button at the right edge of the toast.
 * Keep labels short (one verb), and the handler should be idempotent
 * (toast may auto-dismiss while the click is in flight).
 *
 * The Toaster instance is mounted once in `shared/components/app-shell.tsx`
 * with the project-wide config (dark theme, bottom-right, 4 s default).
 * Use these helpers instead of `import { toast } from 'sonner'` directly so
 * the semantic mapping (sonner's `success`/`error`/`message`/`warning`)
 * lives in one place — easier to retune timing, ordering, or style later.
 */

export type ToastOptions = Parameters<typeof toast>[1]

export type ToastAction = {
  label: string
  onClick: () => void
}

/** Convenience constructor — keeps the `action` shape consistent at call sites
 *  without reaching into sonner's option type. Typical use:
 *
 *    notify.ok('Cancelada', notifyAction('Deshacer', () => restore(id)))
 */
export function notifyAction(label: string, onClick: () => void): ToastOptions {
  return { action: { label, onClick } }
}

export const notify = {
  /** Confirmation of a successful state change. */
  ok(message: string, opts?: ToastOptions) {
    return toast.success(message, opts)
  },
  /** Soft warning — non-blocking issue the user should notice. */
  warn(message: string, opts?: ToastOptions) {
    return toast.warning(message, opts)
  },
  /** Error — operation failed; user must retry or take action. */
  bad(message: string, opts?: ToastOptions) {
    return toast.error(message, opts)
  },
  /** Neutral information — no action implied. */
  info(message: string, opts?: ToastOptions) {
    return toast(message, opts)
  },
  /** Promise — `loading` while pending, `success`/`error` on settle. */
  promise: toast.promise,
  /** Dismiss a specific toast (id from a previous `notify.*` call). */
  dismiss: toast.dismiss,
} as const

// Re-export the raw sonner `toast` for cases needing the full API
// (custom JSX, action buttons, etc.). New code should prefer `notify`.
export { toast }
